const {
  generateEntryUrl,
  generateUrl,
  generateSubPageUrl,
  generateImageUrl,
} = require('./generate-url-functions');

const { normalizeTrailingSlash } = require('./helper-functions');

const { contentTypeMap } = require('../config/content-type-map');
const { processTooltipData } = require('./processTooltipData');

const emptyObjectsFilter = item => {
  return !!item && Object.keys(item).length !== 0;
};

const transformHomepageContent = (
  homepageData,
  localeCode,
  staticTranslations
) => {
  try {
    let transformedContent = {};

    /* Masthead data */
    if (homepageData.masthead) {
      transformedContent.masthead = {
        title: homepageData.masthead.title,
        description: homepageData.masthead.description.description,
        enableLightText: homepageData.masthead.enableLightText,
        cta: homepageData.masthead.callToAction,
        backgroundVideoUrl: homepageData.masthead.backgroundVideoUrl,
        videoBackgroundImage: homepageData.masthead.videoBackgroundImage,
        backgroundColour: homepageData.masthead.backgroundColour,
        mobileImages: homepageData.masthead.mobileImages.map(image => ({
          ...image.gatsbyImageData,
          ...image,
        })),
        tabletImages: homepageData.masthead.tabletImages.map(image => ({
          ...image.gatsbyImageData,
          ...image,
        })),
        desktopImages: homepageData.masthead.desktopImages.map(image => ({
          ...image.gatsbyImageData,
          ...image,
        })),
      };
    }

    return transformedContent;
  } catch (error) {
    console.error(
      'Error in transform-data-functions.js -> transformHomePageContent: ',
      error
    );
    return null;
  }
};

const getTileEnabledState = (rawTile, contentType) => {
  if (
    contentType === 'standardPage' ||
    contentType === 'circularExamplePage' ||
    contentType === 'webflowPage' ||
    contentType === 'resource'
  ) {
    return rawTile.enabled === null ? false : rawTile.enabled;
  } else if (contentType === 'subPage') {
    if (rawTile.enabled && rawTile.parentStructure?.enabled) {
      return true;
    } else {
      return false;
    }
  } else {
    console.warn(
      'getTileEnabledState: contentType is invalid or null. contentType=',
      contentType
    );
    return false;
  }
};

const getTileRenderState = (localeCode, rawTile, contentType) => {
  const isTileEnabled = getTileEnabledState(rawTile, contentType);
  if (localeCode === 'en-GB') {
    if (isTileEnabled) {
      return 'standard tile';
    } else {
      return 'no tile';
    }
  } else {
    if (isTileEnabled) {
      return 'standard tile';
    } else {
      return 'runtime tile';
    }
  }
};

const transformTileContent = (tilesData, localeCode, staticTranslations) => {
  const tiles = [];

  const pickTileImage = rawTile =>
    rawTile.listingImage ||
    rawTile.mastheadImage ||
    rawTile.mastHeadBackgroundImage;

  /**
   * NEW:
   * Return the *variants object* for Gatsby images (aliases live on listingImage/image).
   * If the query is old or a tile has no image, this can be undefined.
   */
  const pickGatsbyVariants = (rawTile, contentType) => {
    // resource tiles use `image`, pages use `listingImage`
    const source =
      contentType === 'resource' ? rawTile.image : rawTile.listingImage;

    // If query is updated, source will be the alias object:
    // { grid2, grid3, grid45, compact, carousel2, carousel3, carousel4, carousel5, ... }
    // If query is old, source might still have gatsbyImageData (legacy) – support both safely.
    if (!source) return undefined;

    // legacy support (in case some queries haven't been updated yet)
    if (source.gatsbyImageData) return source.gatsbyImageData;

    // NEW alias object
    return source;
  };

  /**
   * Always keep a plain URL fallback for non-gatsby cases.
   */
  const pickImageUrl = tileImage =>
    tileImage && tileImage.file ? tileImage.file.url : undefined;

  tilesData &&
    tilesData
      .filter(rawTile => !!rawTile.id)
      .forEach(rawTile => {
        const contentType = contentTypeMap[rawTile.internal.type];
        const gqlContentType = contentTypeMap[rawTile.internal.type];

        const tileRenderState = getTileRenderState(
          localeCode,
          rawTile,
          contentType
        );
        const archivedContent = rawTile.flagAsOldContent;
        const tileContentType = rawTile.resourceContentType;
        const tileGeography = rawTile.geography;
        const tileUpdatedAt = rawTile.updatedAt;

        if (tileRenderState === 'standard tile') {
          const tileImage = pickTileImage(rawTile);

          // NEW: grab the alias object (or legacy gatsbyImageData) and pass it through
          const gatsbyImageData = pickGatsbyVariants(rawTile, contentType);

          // Keep URL fallback for gifs / older content / if a fragment is still legacy
          const imageUrl =
            contentType === 'resource'
              ? pickImageUrl(rawTile.image)
              : pickImageUrl(tileImage);

          const imageAlt =
            contentType === 'resource'
              ? rawTile.image?.description
              : tileImage?.description;

          const imageFormat =
            contentType === 'resource'
              ? (rawTile.image?.file?.contentType ?? null)
              : (tileImage?.file?.contentType ?? null);

          if (contentType === 'circularExamplePage') {
            const tileUrl = generateUrl(
              localeCode,
              'circularExamplePage',
              rawTile.slug
            );

            tiles.push({
              renderType: 'standard',
              id: rawTile.id,
              tileContentType,
              flagAsOldContent: archivedContent,
              tileGeography,
              tileUpdatedAt,
              contentfulId: rawTile.contentful_id,
              gqlContentType,
              resourceContentType: rawTile.resourceContentType,
              localeCode,
              contentType: rawTile.contentType,
              url: tileUrl,
              title: rawTile.listingTitle,
              description: rawTile.listingDescription,
              gatsbyImageData, // pass variants object through
              imageUrl, // keep fallback
              imageAlt,
              imageFormat,
              tags: transformTags(rawTile.topics),
            });
          } else if (contentType === 'standardPage') {
            const tileUrl = generateUrl(
              localeCode,
              rawTile.pageType,
              rawTile.slug
            );

            tiles.push({
              renderType: 'standard',
              id: rawTile.id,
              tileContentType,
              flagAsOldContent: archivedContent,
              tileGeography,
              tileUpdatedAt,
              contentfulId: rawTile.contentful_id,
              gqlContentType,
              resourceContentType: rawTile.resourceContentType,
              localeCode,
              url: tileUrl,
              contentType: rawTile.pageType,
              title: rawTile.listingTitle,
              description: rawTile.listingDescription,
              gatsbyImageData,
              imageUrl,
              imageAlt,
              imageFormat,
              tags: transformTags(rawTile.topics),
              eventDate: rawTile.eventDate,
              isUpcoming: rawTile.isUpcoming,
            });
          } else if (contentType === 'subPage') {
            if (rawTile.parentStructure) {
              const tileUrl = generateSubPageUrl(
                localeCode,
                rawTile.parentStructure.type,
                rawTile.parentStructure.slug,
                rawTile.slug
              );

              tiles.push({
                renderType: 'standard',
                id: rawTile.id,
                tileContentType,
                flagAsOldContent: archivedContent,
                tileGeography,
                tileUpdatedAt,
                resourceContentType: rawTile.resourceContentType,
                contentfulId: rawTile.contentful_id,
                gqlContentType,
                localeCode,
                contentType: rawTile.contentType,
                url: tileUrl,
                title: rawTile.listingTitle,
                description: rawTile.listingDescription,
                gatsbyImageData,
                imageUrl,
                imageAlt,
                imageFormat,
                tags: transformTags(rawTile.topics),
              });
            } else {
              console.warn(
                `Tile subpage parent structure set as null. Tile not rendered: `,
                rawTile
              );
            }
          } else if (contentType === 'webflowPage') {
            tiles.push({
              renderType: 'standard',
              id: rawTile.id,
              tileContentType,
              flagAsOldContent: archivedContent,
              tileGeography,
              tileUpdatedAt,
              contentfulId: rawTile.contentful_id,
              gqlContentType,
              resourceContentType: rawTile.resourceContentType,
              localeCode,
              url: generateUrl(localeCode, rawTile.pageType, rawTile.slug),
              contentType: rawTile.pageType,
              title: rawTile.listingTitle,
              description: rawTile.listingDescription,
              gatsbyImageData,
              imageUrl: rawTile.listingImage?.file?.url || imageUrl,
              imageAlt: rawTile.listingImage?.description || imageAlt,
              imageFormat:
                rawTile.listingImage?.file?.contentType ?? imageFormat,
              tags: transformTags(rawTile.topics),
            });
          } else if (contentType === 'resource' && rawTile.title) {
            let resourceType;
            switch (rawTile.type) {
              case 'internal link':
                resourceType = 'internalLink';
                break;
              case 'external link':
                resourceType = 'externalLink';
                break;
              case 'download':
                resourceType = 'download';
                break;
              default:
                resourceType = null;
            }

            tiles.push({
              renderType: 'standard',
              id: rawTile.id,
              tileContentType,
              flagAsOldContent: archivedContent,
              tileGeography,
              tileUpdatedAt,
              contentfulId: rawTile.contentful_id,
              gqlContentType,
              resourceContentType: rawTile.resourceContentType,
              localeCode,
              url:
                rawTile.bynderFile?.[0]?.original || rawTile.url || undefined,
              contentType: resourceType,
              title: rawTile.title,
              description: rawTile.description
                ? rawTile.description
                : undefined,
              gatsbyImageData, // resource image variants object
              imageUrl, // fallback
              imageAlt,
              imageFormat,
              tags: transformTags(rawTile.topics),
            });
          } else {
            console.error(
              `transform-data-functions -> transformTileContent: contentType: ${contentType} not found. Tile data: `,
              rawTile
            );
          }
        } else if (tileRenderState === 'runtime tile') {
          tiles.push({
            renderType: 'runtime',
            id: rawTile.id,
            contentfulId: rawTile.contentful_id,
            contentType,
          });
        } else if (tileRenderState === 'no tile') {
          console.warn(
            `Tile render state: ${tileRenderState}. Tile not rendered: `,
            rawTile
          );
        } else {
          console.warn(
            `transform-data-functions –> transformTileContent. tileRenderState not matched: `,
            tileRenderState,
            rawTile
          );
        }
      });

  return tiles;
};
const getPodcastEmbedLink = (mediaSrc, simplecastData) => {
  /* Matches contentful media with simplecast entry to generate embed link */
  if (simplecastData.length === 0)
    return null; /* WIP: Defensive coding to fix preview */

  const simplecastSlug = mediaSrc.split('/episodes/')[1];
  const simplecastEntry = simplecastData.filter(
    entry => entry.slug === simplecastSlug
  )[0];

  const simplecastId =
    simplecastEntry.id && simplecastEntry.id.includes('PodcastEpisode__')
      ? simplecastEntry.id.split('PodcastEpisode__')[1]
      : simplecastEntry.id;
  return `https://player.simplecast.com/${simplecastId}`;
};

const getPagePodcastSlugs = pageData => {
  let slugs = [];

  const processMediaSrc = mediaSrc => {
    if (typeof mediaSrc !== 'string') return;
    if (!mediaSrc.includes('simplecast')) return;

    if (!mediaSrc.includes('/episodes/')) {
      console.warn(`Simplecast link passed in the wrong format: ${mediaSrc}`);
      return;
    }

    slugs.push(mediaSrc.split('/episodes/')[1]);
  };

  const processRichTextReferences = references => {
    if (!Array.isArray(references)) return;
    if (references.length === 0) return;

    references.forEach(reference => {
      if (reference.internal?.type === 'ContentfulMediaBlock') {
        processMediaSrc(reference.mediaSrc);
      }
    });
  };

  /* Masthead podcast  */
  processMediaSrc(pageData.mediaSrc);

  /* Flexible content podcasts */
  if (pageData.flexibleContent && pageData.flexibleContent.length > 0) {
    pageData.flexibleContent.forEach(block => {
      processMediaSrc(block.mediaSrc);

      if (block.internal?.type === 'ContentfulRichTextBlock') {
        processRichTextReferences(block.content?.references);
      } else if (block.internal?.type === 'ContentfulAccordionBlock') {
        block.items.forEach(item => {
          processRichTextReferences(item?.content?.references);
        });
      }
    });
  }

  /* Top-level rich text podcast */
  if (pageData.mainContent) {
    processRichTextReferences(pageData.mainContent.references);

    if (Array.isArray(pageData.mainContent.references)) {
      pageData.mainContent.references.forEach(reference => {
        if (reference.internal?.type === 'ContentfulAccordionBlock') {
          reference.items.forEach(item => {
            processRichTextReferences(item?.content?.references);
          });
        }
      });
    }
  }

  return [...new Set(slugs)];
};

const RICH_TEXT_SUPPORTED_INTERNAL_LINK_TYPES = [
  'ContentfulStandardPage',
  'ContentfulSubPage',
  'ContentfulCircularExamplePage',
  'ContentfulWebflowPage',
];

const transformSingleCtaBlockContent = (
  block,
  localeCode,
  pageColour,
  simplecastData,
  staticTranslations,
  rawTooltipData
) => {
  const {
    internal,
    contentful_id,
    title,
    description,
    backgroundColor,
    backgroundVideoUrl,
    videoBackgroundImage,
    content,
    alignment,
    modalLink,
    linkOneText,
    linkTwoText,
    backgroundImage,
    mobileBackgroundImage,
    linkOneInternalPath,
    modalLinkTwo,
    linkTwoInternalPath,
    linkOnePath,
    linkTwoPath,
  } = block;

  const obj = {
    contentType: internal.type,
    contentfulId: contentful_id,
    title,
    description: description?.description,
    content: { raw: content?.raw },
    backgroundColor,
    backgroundVideoUrl: backgroundVideoUrl || undefined,
    videoBackgroundImage: videoBackgroundImage || undefined,
    gatsbyImageData: backgroundImage?.gatsbyImageData || undefined,
    gatsbyMobileImageData:
      mobileBackgroundImage?.gatsbyImageData ||
      backgroundImage?.gatsbyImageData ||
      undefined,
    format: backgroundImage?.file?.contentType || undefined,
    mobileFormat:
      mobileBackgroundImage?.file?.contentType ||
      backgroundImage?.file?.contentType ||
      undefined,
    imageSrc: backgroundImage?.file?.url || null,
    mobileImageSrc:
      mobileBackgroundImage?.file?.url || backgroundImage?.file?.url || null,
    imageAlt: backgroundImage?.description || null,
    alignment,
    modalLink: modalLink
      ? transformedModalBlock(modalLink, {
          localeCode,
          pageColour,
          simplecastData,
          staticTranslations,
          rawTooltipData,
        })
      : null,
    linkOneText,
    modalLinkTwo: modalLinkTwo
      ? transformedModalBlock(modalLinkTwo, {
          localeCode,
          pageColour,
          simplecastData,
          staticTranslations,
          rawTooltipData,
        })
      : null,
    linkTwoText,
    showWhiteBox: block.showWhiteBox || false,
    useLightText: block.useLightText || false,
  };

  if (linkOneInternalPath) {
    obj.linkOnePath = generateEntryUrl(localeCode, {
      ...linkOneInternalPath[0],
      contentType: linkOneInternalPath[0].internal.type,
    });
    obj.isLinkOneExternal = false;
  }

  if (linkTwoInternalPath) {
    obj.linkTwoPath = generateEntryUrl(localeCode, {
      ...linkTwoInternalPath[0],
      contentType: linkTwoInternalPath[0].internal.type,
    });
    obj.isLinkTwoExternal = false;
  }

  if (linkOnePath) {
    obj.linkOnePath = linkOnePath;
    obj.isLinkOneExternal = true;
  }

  if (linkTwoPath) {
    obj.linkTwoPath = linkTwoPath;
    obj.isLinkTwoExternal = true;
  }

  if (Array.isArray(content?.references) && content.references.length > 0) {
    const transformedReferences = content.references
      .filter(emptyObjectsFilter)
      .map(
        transformRichTextReference({
          pageColour,
          localeCode,
          simplecastData,
          staticTranslations,
          rawTooltipData,
        })
      );

    obj.content.entryReferences =
      transformedReferences.filter(emptyObjectsFilter);
  }

  return obj;
};

const transformedLegendBlock = block => {
  return {
    contentfulId: block.contentful_id,
    contentType: block.internal.type,
    logoSrc: block.logo?.file?.url || undefined,
    companyName: block.companyName,
    origin: block.origin,
    founded: block.founded,
    website: block.website,
  };
};

const transformRichTextContent = ({
  content,
  pageColour,
  localeCode,
  simplecastData,
  staticTranslations,
  rawTooltipData,
  videoMeta,
}) => {
  if (!content) {
    return {
      raw: undefined,
      entryReferences: [],
    };
  }

  return {
    ...content,
    raw: content.raw,
    references: content.references || [],
    entryReferences: Array.isArray(content.references)
      ? content.references
          .filter(emptyObjectsFilter)
          .map(
            transformRichTextReference({
              pageColour,
              localeCode,
              simplecastData,
              staticTranslations,
              rawTooltipData,
              videoMeta,
            })
          )
          .filter(emptyObjectsFilter)
      : [],
  };
};

const transformModalRefs = ({
  block,
  localeCode,
  pageColour,
  simplecastData,
  staticTranslations,
  rawTooltipData,
}) => {
  if (!block.content?.references || !Array.isArray(block.content.references)) {
    return [];
  }

  return block.content.references
    .filter(emptyObjectsFilter)
    .map(
      transformRichTextReference({
        pageColour,
        localeCode,
        simplecastData,
        staticTranslations,
        rawTooltipData,
      })
    )
    .filter(emptyObjectsFilter);
};

const transformedModalBlock = (
  block,
  { localeCode, pageColour, simplecastData, staticTranslations, rawTooltipData }
) => {
  const modalColour = block.modalUseLightText ? '#fff' : '#2f2f46';

  const modalEntryReferences = transformModalRefs({
    block,
    localeCode,
    pageColour: modalColour,
    simplecastData,
    staticTranslations,
    rawTooltipData,
  }).filter(emptyObjectsFilter);

  const modalBlock = {
    contentfulId: block.contentful_id,
    contentType: block.internal.type,
    isTextLink: block.isTextLink,
    useLightText: block.useLightText,
    linkText: block.linkText,

    content: {
      ...block.content,
      raw: block.content?.raw,
      references: block.content?.references || [],
      entryReferences: modalEntryReferences,
    },

    backgroundColor: block.backgroundColor || 'transparent',
    backgroundImage: block.backgroundImage,
    mobileBackgroundImage: block.mobileBackgroundImage,
    colour: modalColour,
  };

  return modalBlock;
};

const transformTileBlockContent = (
  block,
  localeCode,
  staticTranslations,
  pageColour
) => {
  return {
    contentfulId: block.contentful_id,
    contentType: block.internal.type,
    visibleTilesCount: block.visibleTilesCount
      ? Number(block.visibleTilesCount.replace(' tiles', ''))
      : 5,
    title: block.title,
    anchorId: block.anchorId,
    description: block.descriptionShort,
    isCarousel: block.displayAsCarousel,
    cta: {
      text: block.callToActionText,
      url: block.callToActionLink,
    },
    tiles: transformTileContent(block.tiles, localeCode, staticTranslations),
    colour: pageColour,
    hasDottedLines: block.hasDottedLines,
  };
};

const transformInteractiveImageBlockContent = block => {
  return {
    contentfulId: block.contentful_id,
    contentType: block.internal.type,
    imageHeight: block.imageHeight,
    imageWidth: block.imageWidth,
    interactiveImagePinBlocks: block.interactiveImagePinBlocks,
    image: block.interactiveImage?.file.url,
    title: block.title,
  };
};

const transformEmbedBlockContent = (block, pageColour) => {
  return {
    contentfulId: block.contentful_id,
    contentType: block.internal.type,
    titleCmsOnly: block.titleCmsOnly,
    title: block.title,
    displayTitle: block.displayTitle,
    anchorId: block.anchorId,
    description: block.shortDescription,
    url:
      block.bynderFile?.[0]?.original || block.embedUrl?.embedUrl || undefined,
    isFullWidth: block.isFullWidth,
    mobileHeight: block.mobileHeight,
    largeMobileHeight: block.largeMobileHeight,
    smallTabletHeight: block.smallTabletHeight,
    tabletHeight: block.tabletHeight,
    largeTabletHeight: block.largeTabletHeight,
    smallDesktopHeight: block.smallDesktopHeight,
    desktopHeight: block.desktopHeight,
    colour: pageColour,
  };
};

const transformExample = localeCode => example => {
  const {
    id,
    shortTitle: tabTitle,
    summaryTitle: quote,
    summary: body,
    slug,
    listingImage,
    logo,
    internal: { type },
  } = example;

  const exampleUrl = generateUrl(localeCode, contentTypeMap[type], slug);

  return {
    id,
    tabTitle,
    quote,
    body,
    cta: {
      text: 'explore the example',
      url: exampleUrl,
      newtab: false, // @TODO handle external Circular Examples
    },
    image: {
      src: listingImage?.file?.url || '',
      alt: listingImage?.description || '',
      format: listingImage?.file?.contentType || undefined,
      gatsbyImageData: listingImage?.gatsbyImageData || undefined,
    },
    logo: {
      src: logo?.file?.url || '',
      alt: logo?.description || '',
      format: logo?.file?.contentType || undefined,
      gatsbyImageData: logo?.gatsbyImageData || undefined,
    },
  };
};

const transformCircularExamplesBlock = (block, pageColour, localeCode) => {
  const {
    contentful_id,
    internal: { type },
    title,
    anchorId,
    descriptionShort,
    examples,
  } = block;

  return {
    contentfulId: contentful_id,
    contentType: type,
    title,
    anchorId,
    subtitle: descriptionShort,
    colour: pageColour,
    examples: Array.isArray(examples)
      ? examples.map(transformExample(localeCode))
      : undefined,
  };
};

const transformAutomatedTileListingBlock = (
  block,
  localeCode,
  staticTranslations,
  pageColour,
  currentEntryId
) => {
  const {
    manualTilesOverwrite,
    contentful_id,
    internal: { type },
  } = block;
  const manualTilesOverwritten = manualTilesOverwrite
    ? transformTileContent(manualTilesOverwrite, localeCode, staticTranslations)
    : [];

  const visibleTilesCount = block.visibleTilesCount
    ? Number(block.visibleTilesCount.replace(' tiles', ''))
    : 5;

  return {
    contentfulId: contentful_id,
    contentType: type,
    automatedListing: {
      ...block,
      manualTilesOverwritten,
      visibleTilesCount,
    },
    pageColour,
    localeCode,
    currentEntryId,
    hasDottedLines: block.hasDottedLines,
  };
};

const transformAccordionBlock = ({
  block,
  localeCode,
  staticTranslations,
  pageColour,
  simplecastData,
  rawTooltipData,
}) => {
  const {
    contentful_id,
    internal: { type },
  } = block;

  /* Format items for Rich text component */
  let transformedItems = [];
  block.items.forEach(item => {
    let transformedItem = {
      title: item.title ? item.title : 'Click to expand',
      richTextContent: {
        raw: item.content ? item.content.raw : undefined,
      },
      colour: pageColour,
      tooltipData: processTooltipData(rawTooltipData, localeCode),
    };
    /* Logic for Rich text to handle internal links and embedded blocks */
    if (
      Array.isArray(item.content.references) &&
      item.content.references.length > 0
    ) {
      const transformedReferences = item.content.references
        .filter(emptyObjectsFilter)
        .map(
          transformRichTextReference({
            pageColour,
            localeCode,
            simplecastData,
            staticTranslations,
            rawTooltipData,
          })
        );
      transformedItem.richTextContent.entryReferences =
        transformedReferences.filter(emptyObjectsFilter);
    }
    /* Create package with all formated Rich text content */
    transformedItems.push(transformedItem);
  });

  return {
    contentfulId: contentful_id,
    contentType: type,
    title: block.title,
    anchorId: block.anchorId,
    greyBackground: block.greyBackground,
    items: transformedItems,
  };
};

const transformLogoBlock = ({ block, pageColour, localeCode }) => {
  const {
    contentful_id,
    internal: { type },
    title,
    anchorId,
    logos,
    displayAsCarousel,
  } = block;
  let transformedItems = [];

  if (Array.isArray(logos) && logos.length > 0) {
    transformedItems = logos.map(logo => {
      const gatsbyImageData = logo.logo?.gatsbyImageData;
      const logoData = {
        name: logo.name,
        type: logo.type,
        gatsbyImageData: gatsbyImageData || undefined,
        logoSrc: logo.logo?.file?.url,
        logoFormat: logo.logo?.file?.contentType || undefined,
        logoAlt: logo.description,
        linkText: logo.linkText,
      };

      if (logo.organisationPage && logo.organisationPage.internal) {
        const entryData = {
          ...logo.organisationPage,
          contentType: logo.organisationPage.internal.type,
        };
        const organisationPageUrl = generateEntryUrl(localeCode, entryData);
        logoData.url = organisationPageUrl || undefined;
      }

      return logoData;
    });
  }

  return {
    contentfulId: contentful_id,
    contentType: type,
    anchorId,
    title,
    displayAsCarousel: displayAsCarousel,
    colour: pageColour,
    logos: transformedItems,
  };
};

const transformCustomBlock = ({ block }) => {
  const {
    contentful_id,
    internal: { type },
    anchorId,
    customBlockId,
  } = block;

  return {
    contentfulId: contentful_id,
    contentType: type,
    customBlockId,
    anchorId,
  };
};

const transformTwoColumnTextBlockReference = ({
  reference,
  localeCode,
  staticTranslations,
  rawTooltipData,
  pageColour,
  simplecastData,
}) => {
  return {
    contentfulId: reference.contentful_id,
    contentType: reference.internal.type,
    title: reference.title,
    anchorId: reference.anchorId,
    colour: reference.useWhiteText ? '#ffffff' : '#2f2f46',
    backgroundColor: reference.backgroundColor,
    backgroundImage: reference.backgroundImage?.file
      ? {
          src: reference.backgroundImage.file.url,
          alt: reference.backgroundImage.description || '',
          format: reference.backgroundImage.file.contentType,
          gatsbyImageData: reference.backgroundImage.gatsbyImageData,
        }
      : undefined,
    mobileBackgroundImage: reference.mobileBackgroundImage?.file
      ? {
          src: reference.mobileBackgroundImage.file.url,
          alt: reference.mobileBackgroundImage.description || '',
          format: reference.mobileBackgroundImage.file.contentType,
          gatsbyImageData: reference.mobileBackgroundImage.gatsbyImageData,
        }
      : undefined,

    leftText: {
      raw: reference.leftText?.raw,
      entryReferences: Array.isArray(reference.leftText?.references)
        ? reference.leftText.references
            .filter(emptyObjectsFilter)
            .map(
              transformRichTextReference({
                pageColour,
                localeCode,
                simplecastData,
                staticTranslations,
                rawTooltipData,
              })
            )
            .filter(emptyObjectsFilter)
        : [],
    },

    rightText: {
      raw: reference.rightText?.raw,
      entryReferences: Array.isArray(reference.rightText?.references)
        ? reference.rightText.references
            .filter(emptyObjectsFilter)
            .map(
              transformRichTextReference({
                pageColour,
                localeCode,
                simplecastData,
                staticTranslations,
                rawTooltipData,
              })
            )
            .filter(emptyObjectsFilter)
        : [],
    },
  };
};

const transformRichTextReference =
  ({
    pageColour,
    localeCode,
    simplecastData,
    staticTranslations,
    rawTooltipData,
    videoMeta,
  }) =>
  reference => {
    if (reference.internal?.type === 'ContentfulCallToActionBlock') {
      const baseProps = {
        contentfulId: reference.contentful_id,
        title: reference.title,
        anchorId: reference.anchorId,
        description: reference?.description?.description || null,
        buttonText: reference.buttonText,
        externalUrl: reference.externalUrl,
        secondButtonText: reference.secondButtonText,
        secondExternalUrl: reference.secondExternalUrl,
        contentType: reference.internal.type,
        colour: pageColour,
      };

      let entryData = { ...baseProps };

      if (reference?.internalPageLink?.internal) {
        const entityData = {
          ...baseProps,
          ...reference.internalPageLink,
          contentType: reference.internalPageLink.internal.type,
        };
        const internalUrl = generateEntryUrl(localeCode, entityData);

        if (internalUrl) {
          entryData = {
            ...entryData,
            internalUrl,
          };
        }
      }

      if (reference?.secondInternalPageLink?.internal) {
        const entityData = {
          ...baseProps,
          ...reference.secondInternalPageLink,
          contentType: reference.secondInternalPageLink.internal.type,
        };
        const secondInternalUrl = generateEntryUrl(localeCode, entityData);

        if (secondInternalUrl) {
          entryData = {
            ...entryData,
            secondInternalUrl,
          };
        }
      }

      return entryData;
    } else if (
      RICH_TEXT_SUPPORTED_INTERNAL_LINK_TYPES.includes(reference.internal?.type)
    ) {
      const entryData = {
        ...reference,
        contentType: reference.internal && reference.internal.type,
      };
      const internalUrl = generateEntryUrl(localeCode, entryData);
      return {
        contentfulId: reference.contentful_id,
        slug: internalUrl ? internalUrl : null,
      };
    } else if (reference.internal?.type === 'ContentfulMediaBlock') {
      const isPodcast = reference.mediaSrc?.includes('simplecast');
      const mediaSrc = isPodcast
        ? getPodcastEmbedLink(reference.mediaSrc, simplecastData)
        : reference.mediaSrc;

      if (
        !isPodcast &&
        reference.videoEmbedUrl &&
        reference.videoThumbnailUrl &&
        reference.videoFetchStatus !== 'unsupported video format'
      ) {
        videoMeta &&
          videoMeta.push({
            name: reference.videoTitle || 'Video',
            description:
              reference.videoDescription?.videoDescription || undefined,
            thumbnailUrl: reference.videoThumbnailUrl,
            uploadDate: reference.videoUploadDate || undefined, // ISO 8601
            duration: reference.videoDuration || undefined, // ISO 8601
            embedUrl: reference.videoEmbedUrl,
          });
      }

      return {
        contentfulId: reference.contentful_id,
        anchorId: reference.anchorId,
        contentType: 'ContentfulMediaBlock',
        mediaSrc,
      };
    } else if (reference.internal?.type === 'ContentfulImageBlock') {
      const images = reference.images
        ? reference.images.map(image => {
            return {
              src: image && image.file ? image.file.url : undefined,
              alt: image.description,
              format: image && image.file ? image.file.contentType : undefined,
              gatsbyImageData:
                image && image.gatsbyImageData
                  ? image.gatsbyImageData
                  : undefined,
            };
          })
        : [];
      if (images[0]) {
        images[0].description = reference.firstImageDescription
          ? reference.firstImageDescription
          : undefined;
      }
      if (images[1]) {
        images[1].description = reference.secondImageDescription
          ? reference.secondImageDescription
          : undefined;
      }
      const mobileImages = reference.mobileImages
        ? reference.mobileImages.map(image => {
            return {
              src: image && image.file ? image.file.url : undefined,
              alt: image.description,
              format: image && image.file ? image.file.contentType : undefined,
              gatsbyImageData:
                image && image.gatsbyImageData
                  ? image.gatsbyImageData
                  : undefined,
            };
          })
        : [];

      return {
        contentfulId: reference.contentful_id,
        contentType: 'ContentfulImageBlock',
        title: reference.title,
        anchorId: reference.anchorId,
        images,
        mobileImages,
        colour: pageColour,
        narrowWidth: reference.narrowWidth,
        firstImageLink: reference.firstImageLink,
        firstImageOpenInNewTab: reference.firstImageOpenInNewTab,
        secondImageLink: reference.secondImageLink,
        secondImageOpenInNewTab: reference.secondImageOpenInNewTab,
      };
    } else if (reference.internal?.type === 'ContentfulAlignedImageBlock') {
      const transformedContentBlock = transformAlignedImageBlock({
        block: reference,
        localeCode,
        pageColour,
        simplecastData,
        staticTranslations,
        rawTooltipData,
        videoMeta,
      });

      return transformedContentBlock;
    } else if (reference.internal?.type === 'ContentfulHighlightBlock') {
      return {
        contentfulId: reference.contentful_id,
        anchorId: reference.anchorId,
        quote: reference.quote?.quote,
        author: reference.author,
        contentType: reference.internal.type,
        authorImage: reference.authorImage,
        jobTitleOrganisation: reference.jobTitleOrganisation,
        quoteTextColor: reference.quoteTextColor,
        hasDottedLines: reference.useDottedLines,
        narrowWidth: reference.narrowWidth,
      };
    } else if (reference.internal?.type === 'ContentfulSingleCtaBlock') {
      return transformSingleCtaBlockContent(
        reference,
        localeCode,
        pageColour,
        simplecastData,
        staticTranslations,
        rawTooltipData
      );
    } else if (reference.internal?.type === 'ContentfulTileBlock') {
      return transformTileBlockContent(
        reference,
        localeCode,
        staticTranslations,
        pageColour
      );
    } else if (reference.internal?.type === 'ContentfulInteractiveImageBlock') {
      return transformInteractiveImageBlockContent(reference);
    } else if (reference.internal?.type === 'ContentfulEmbedBlock') {
      return transformEmbedBlockContent(reference, pageColour);
    } else if (reference.internal?.type === 'ContentfulCircularExamplesBlock') {
      return transformCircularExamplesBlock(reference, pageColour, localeCode);
    } else if (
      reference.internal?.type === 'ContentfulAutomatedListingTileBlock'
    ) {
      return transformAutomatedTileListingBlock(
        reference,
        localeCode,
        staticTranslations,
        pageColour
      );
    } else if (reference.internal?.type === 'ContentfulTwoColumnTextBlock') {
      return transformTwoColumnTextBlockReference({
        reference,
        localeCode,
        staticTranslations,
        rawTooltipData,
        pageColour,
        simplecastData,
      });
    } else if (reference.internal?.type === 'ContentfulAccordionBlock') {
      return transformAccordionBlock({
        block: reference,
        localeCode,
        staticTranslations,
        pageColour,
        simplecastData,
        rawTooltipData,
      });
    } else if (reference.internal?.type === 'ContentfulLogoBlock') {
      return transformLogoBlock({
        block: reference,
        pageColour,
        localeCode,
      });
    } else if (reference.internal?.type === 'ContentfulCustomBlock') {
      return transformCustomBlock({ block: reference });
    } else if (reference.internal?.type === 'ContentfulLegendBlock') {
      return transformedLegendBlock(reference);
    } else if (reference.internal?.type === 'ContentfulFlipcardBlock') {
      return transformFlipcardBlock({
        block: reference,
        pageColour,
        localeCode,
        simplecastData,
        staticTranslations,
        rawTooltipData,
      });
    } else if (reference.internal?.type === 'ContentfulGalleryCarouselBlock') {
      return transformGalleyCarouselBlock(reference, localeCode);
    } else if (
      reference.internal?.type === 'ContentfulRichTextOpeningParagraph'
    ) {
      return {
        contentfulId: reference.contentful_id,
        contentType: reference.internal.type,
        markdownText: reference.markdownText.markdownText,
      };
    } else if (reference.internal?.type === 'ContentfulModalBlock') {
      return transformedModalBlock(reference, {
        localeCode,
        pageColour,
        simplecastData,
        staticTranslations,
        rawTooltipData,
      });
    } else if (reference.internal?.type === 'ContentfulTabbedBlock') {
      const currentEntryId = reference.contentful_id;
      return transformTabbedBlock(
        reference,
        pageColour,
        localeCode,
        staticTranslations,
        currentEntryId
      );
    } else {
      console.warn(
        'Unsupported content type for rich text:',
        reference.internal?.type
      );
      return null;
    }
  };

const transformTopLevelRichText = ({
  richTextContent,
  localeCode,
  pageColour,
  simplecastData,
  rawTooltipData,
  staticTranslations,
}) => {
  try {
    const transformed = {
      content: {
        richTextContent: {
          raw: richTextContent.raw || undefined,
          entryReferences: [],
        },
        colour: pageColour,
        tooltipData: processTooltipData(rawTooltipData, localeCode),
      },
    };

    if (
      !Array.isArray(richTextContent.references) ||
      richTextContent.references.length === 0
    ) {
      return transformed;
    }

    const transformedReferences = richTextContent.references
      .filter(emptyObjectsFilter)
      .map(
        transformRichTextReference({
          pageColour,
          localeCode,
          simplecastData,
          staticTranslations,
          rawTooltipData,
        })
      );

    transformed.content.richTextContent.entryReferences =
      transformedReferences.filter(emptyObjectsFilter);

    return transformed;
  } catch (e) {
    console.error('error in transformTopLevelRichText', e);
  }
};

const transformAlignedImageBlock = ({
  block,
  localeCode,
  pageColour,
  simplecastData,
  staticTranslations,
  rawTooltipData,
  videoMeta,
}) => {
  const {
    contentful_id,
    title,
    anchorId,
    content,
    imageSrc,
    alignImage,
    imageLink,
    imageOpenInNewTab,
    modalLink,
    linkPath,
    linkText,
    internal,
    internalPageLink,
    internalImageLink,
    mediaUrl,
    modalLinkTwo,
    secondButtonText,
    secondButtonPath,
    secondInternalPageLink,
  } = block;

  let linkUrl = linkPath;
  let secondLinkUrl = secondButtonPath;
  let internalImageUrl;

  if (internalPageLink && internalPageLink.internal) {
    const entryData = {
      ...internalPageLink,
      contentType: internalPageLink.internal.type,
    };
    const internalUrl = generateEntryUrl(localeCode, entryData);
    if (internalUrl) linkUrl = internalUrl;
  }

  if (secondInternalPageLink && secondInternalPageLink.internal) {
    const entryData = {
      ...secondInternalPageLink,
      contentType: secondInternalPageLink.internal.type,
    };
    const secondInternalUrl = generateEntryUrl(localeCode, entryData);
    if (secondInternalUrl) secondLinkUrl = secondInternalUrl;
  }

  if (internalImageLink && internalImageLink.internal) {
    const entryData = {
      ...internalImageLink,
      contentType: internalImageLink.internal.type,
    };
    internalImageUrl = generateEntryUrl(localeCode, entryData);
  }

  return {
    contentfulId: contentful_id,
    title,
    anchorId,
    content: transformRichTextContent({
      content,
      pageColour,
      localeCode,
      simplecastData,
      staticTranslations,
      rawTooltipData,
      videoMeta,
    }),
    imageAlt: imageSrc?.description || '',
    imageSrc: imageSrc?.file?.url,
    format: imageSrc?.file?.contentType || undefined,
    gatsbyImageData: imageSrc?.gatsbyImageData || undefined,
    alignImage,
    imageLink,
    imageOpenInNewTab,
    internalImageLink: internalImageUrl,
    contentType: internal?.type,
    mediaUrl,
    modalLink: modalLink
      ? transformedModalBlock(modalLink, {
          localeCode,
          pageColour,
          simplecastData,
          staticTranslations,
          rawTooltipData,
        })
      : null,
    firstButtonText: linkText,
    firstButtonLink: linkUrl,
    modalLinkTwo: modalLinkTwo
      ? transformedModalBlock(modalLinkTwo, {
          localeCode,
          pageColour,
          simplecastData,
          staticTranslations,
          rawTooltipData,
        })
      : null,
    secondButtonText,
    secondButtonLink: secondLinkUrl,
  };
};

const transformTabbedBlock = (
  block,
  pageColour,
  localeCode,
  staticTranslations,
  currentEntryId
) => {
  const {
    contentful_id,
    internal: { type },
    title,
    anchorId,
    description,
    tabs,
  } = block;

  return {
    localeCode,
    contentfulId: contentful_id,
    contentType: type,
    title,
    anchorId,
    description: description?.description,
    colour: pageColour,
    tabs: tabs,
    staticTranslations,
    currentEntryId,
  };
};

const transformFlexibleContent = ({
  flexibleContent,
  localeCode,
  staticTranslations,
  pageColour,
  simplecastData,
  rawTooltipData,
  currentEntryId,
}) => {
  const videoMeta = [];
  try {
    const transformedFlexibleContent = [];
    if (!Array.isArray(flexibleContent)) return null;

    flexibleContent.forEach(block => {
      // Only process blocks that have been identified, with a type, in the GQL query.

      if (block.internal) {
        const transformedContentBlock = {
          id: block.id,
        };

        if (block.internal.type === 'ContentfulSingleCtaBlock') {
          transformedContentBlock.type = 'singleCtaBlock';
          transformedContentBlock.content = transformSingleCtaBlockContent(
            block,
            localeCode,
            pageColour,
            simplecastData,
            staticTranslations,
            rawTooltipData
          );
        } else if (block.internal.type === 'ContentfulModalBlock') {
          transformedContentBlock.type = 'modalBlock';
          transformedContentBlock.content = transformedModalBlock(block, {
            localeCode,
            pageColour,
            simplecastData,
            staticTranslations,
            rawTooltipData,
          });
        } else if (block.internal.type === 'ContentfulLegendBlock') {
          transformedContentBlock.type = 'legendBlock';
          transformedContentBlock.content = transformedLegendBlock(block);
        } else if (block.internal.type === 'ContentfulCallToActionBlock') {
          transformedContentBlock.type = 'callToActionBlock';
          transformedContentBlock.content = {
            title: block.title,
            anchorId: block.anchorId,
            description: block.description
              ? block.description.description
              : null,
            buttonText: block.buttonText,
            externalUrl: block.externalUrl,
            colour: pageColour,
            secondButtonText: block.secondButtonText,
            secondExternalUrl: block.secondExternalUrl,
          };
          if (block.internalPageLink && block.internalPageLink.internal) {
            const entryData = {
              ...block.internalPageLink,
              contentType: block.internalPageLink.internal.type,
            };
            const internalUrl = generateEntryUrl(localeCode, entryData);
            if (internalUrl) {
              transformedContentBlock.content.internalUrl = internalUrl;
            }
          }
          if (
            block.secondInternalPageLink &&
            block.secondInternalPageLink.internal
          ) {
            const entryData = {
              ...block.secondInternalPageLink,
              contentType: block.secondInternalPageLink.internal.type,
            };
            const secondInternalUrl = generateEntryUrl(localeCode, entryData);
            if (secondInternalUrl) {
              transformedContentBlock.content.secondInternalUrl =
                secondInternalUrl;
            }
          }
        } else if (block.internal?.type === 'ContentfulInteractiveImageBlock') {
          transformedContentBlock.type = 'interactiveImageBlock';
          transformedContentBlock.content =
            transformInteractiveImageBlockContent(block);
        } else if (block.internal.type === 'ContentfulEmbedBlock') {
          transformedContentBlock.type = 'embedBlock';
          transformedContentBlock.content = transformEmbedBlockContent(
            block,
            pageColour
          );
        } else if (block.internal.type === 'ContentfulTileBlock') {
          transformedContentBlock.type = 'tileBlock';
          transformedContentBlock.content = transformTileBlockContent(
            block,
            localeCode,
            staticTranslations,
            pageColour
          );
        } else if (
          block.internal.type === 'ContentfulAutomatedListingTileBlock'
        ) {
          transformedContentBlock.type = 'automatedListingTileBlock';
          transformedContentBlock.content = transformAutomatedTileListingBlock(
            block,
            localeCode,
            staticTranslations,
            pageColour,
            currentEntryId
          );
        } else if (block.internal.type === 'ContentfulCircularExamplesBlock') {
          transformedContentBlock.type = 'circularExamplesBlock';
          transformedContentBlock.content = transformCircularExamplesBlock(
            block,
            pageColour,
            localeCode
          );
        } else if (block.internal.type === 'ContentfulHighlightBlock') {
          transformedContentBlock.type = 'highlightBlock';
          transformedContentBlock.content = {
            anchorId: block.anchorId,
            quote: block.quote ? block.quote.quote : undefined,
            author: block.author,
            colour: pageColour,
            authorImage: block.authorImage,
            jobTitleOrganisation: block.jobTitleOrganisation,
            quoteTextColor: block.quoteTextColor,
            hasDottedLines: block.useDottedLines,
            narrowWidth: block.narrowWidth,
          };
        } else if (block.internal.type === 'ContentfulDownloadBlock') {
          transformedContentBlock.type = 'downloadBlock';
          transformedContentBlock.content = {
            title: block.title,
            anchorId: block.anchorId,
            description: block.description && block.description.description,
            downloads: block.downloads[0],
            backgroundImageUrl:
              block.backgroundImage && block.backgroundImage.file
                ? block.backgroundImage.file.url
                : undefined,
            imageAlt: block.backgroundImage
              ? block.backgroundImage.description
              : undefined,
            imageFormat:
              block.backgroundImage && block.backgroundImage.file
                ? block.backgroundImage.file.contentType
                : undefined,
            gatsbyImageData: block.backgroundImage?.gatsbyImageData,
            colour: pageColour,
          };
        } else if (block.internal.type === 'ContentfulFlipcardBlock') {
          transformedContentBlock.type = 'flipcardBlock';
          transformedContentBlock.content = transformFlipcardBlock({
            block,
            pageColour,
            localeCode,
            simplecastData,
            staticTranslations,
            rawTooltipData,
          });

          if (
            block?.content?.references &&
            Array.isArray(block.content.references) &&
            block.content.references.length > 0
          ) {
            const transformedReferences = block.content.references
              .filter(emptyObjectsFilter)
              .map(
                transformRichTextReference({
                  pageColour,
                  localeCode,
                  simplecastData,
                  staticTranslations,
                  rawTooltipData,
                })
              );
            // note using "entryReferences" as "references" field affects the rich text node internal workings...
            transformedContentBlock.content.content.entryReferences =
              transformedReferences.filter(emptyObjectsFilter);
          }
        } else if (block.internal.type === 'ContentfulLogoBlock') {
          transformedContentBlock.type = 'logoBlock';
          transformedContentBlock.content = transformLogoBlock({
            block,
            pageColour,
            localeCode,
          });
        } else if (block.internal.type === 'ContentfulMediaBlock') {
          const rawSource = block.mediaSrc;
          let mediaSrc;
          if (rawSource.includes('youtube') || rawSource.includes('youtu.be')) {
            mediaSrc = rawSource;
          } else if (rawSource.includes('vimeo')) {
            mediaSrc = rawSource;
          } else if (rawSource.includes('simplecast')) {
            mediaSrc = getPodcastEmbedLink(rawSource, simplecastData);
          } else {
            return console.error(
              `Media Block called with invalid source: ${block.mediaSrc}`
            );
          }
          transformedContentBlock.type = 'mediaBlock';
          transformedContentBlock.content = {
            mediaSrc,
            colour: pageColour,
            anchorId: block.anchorId,
          };

          // --- NEW: collect video info for SEO if it's YouTube/Vimeo ---
          if (
            rawSource.includes('youtube') ||
            rawSource.includes('youtu.be') ||
            rawSource.includes('vimeo')
          ) {
            if (
              block.videoEmbedUrl &&
              block.videoThumbnailUrl &&
              block.videoFetchStatus !== 'unsupported video format'
            ) {
              videoMeta.push({
                name: block.videoTitle || 'Video',
                description:
                  block.videoDescription?.videoDescription || undefined,
                thumbnailUrl: block.videoThumbnailUrl,
                uploadDate: block.videoUploadDate || undefined, // ISO 8601
                duration: block.videoDuration || undefined, // ISO 8601
                embedUrl: block.videoEmbedUrl,
              });
            }
          }
          // --- END NEW ---
        } else if (block.internal.type === 'ContentfulRichTextBlock') {
          const richTextColour = block.useWhiteText ? '#ffffff' : '#2f2f46';

          transformedContentBlock.type = 'textBlock';
          transformedContentBlock.content = {
            title: block.title,
            isFirstBlock: block.isFirstBlock,
            anchorId: block.anchorId,
            richTextContent: {
              raw: block.content ? block.content.raw : undefined,
            },
            colour: richTextColour,
            tooltipData: processTooltipData(rawTooltipData, localeCode),
            backgroundColor: block.backgroundColor,
            backgroundVideoUrl: block.backgroundVideoUrl,
            videoBackgroundImage: block.videoBackgroundImage,
            backgroundImage: block.backgroundImage?.file
              ? {
                  src: block.backgroundImage.file.url,
                  alt: block.backgroundImage.description || '',
                  format: block.backgroundImage.file.contentType,
                  gatsbyImageData: block.backgroundImage.gatsbyImageData,
                }
              : undefined,
            mobileBackgroundImage: block.mobileBackgroundImage?.file
              ? {
                  src: block.mobileBackgroundImage.file.url,
                  alt: block.mobileBackgroundImage.description || '',
                  format: block.mobileBackgroundImage.file.contentType,
                  gatsbyImageData: block.mobileBackgroundImage.gatsbyImageData,
                }
              : undefined,
          };
          // Internal entry links and embedded blocks require additional references data. Add if present:
          if (
            Array.isArray(block.content.references) &&
            block.content.references.length > 0
          ) {
            const transformedReferences = block.content.references
              .filter(emptyObjectsFilter)
              .map(
                transformRichTextReference({
                  pageColour: richTextColour,
                  localeCode,
                  simplecastData,
                  staticTranslations,
                  rawTooltipData,
                  videoMeta,
                })
              );

            // note using "entryReferences" as "references" field affects the rich text node internal workings...
            transformedContentBlock.content.richTextContent.entryReferences =
              transformedReferences.filter(emptyObjectsFilter);
          }
        } else if (block.internal.type === 'ContentfulImageBlock') {
          transformedContentBlock.type = 'imageBlock';
          const images = block.images
            ? block.images.map(image => {
                return {
                  src: image && image.file ? image.file.url : undefined,
                  alt: image.description,
                  format:
                    image && image.file ? image.file.contentType : undefined,
                  gatsbyImageData:
                    image && image.gatsbyImageData
                      ? image.gatsbyImageData
                      : undefined,
                };
              })
            : [];
          const mobileImages = block.mobileImages
            ? block.mobileImages.map(image => {
                return {
                  src: image && image.file ? image.file.url : undefined,
                  alt: image.description,
                  format:
                    image && image.file ? image.file.contentType : undefined,
                  gatsbyImageData:
                    image && image.gatsbyImageData
                      ? image.gatsbyImageData
                      : undefined,
                };
              })
            : [];

          if (images[0]) {
            images[0].description = block.firstImageDescription
              ? block.firstImageDescription
              : undefined;
          }
          if (images[1]) {
            images[1].description = block.secondImageDescription
              ? block.secondImageDescription
              : undefined;
          }
          if (mobileImages[0]) {
            mobileImages[0].description = block.firstMobileImageDescription
              ? block.firstMobileImageDescription
              : undefined;
          }
          if (mobileImages[1]) {
            mobileImages[1].description = block.secondMobileImageDescription
              ? block.secondMobileImageDescription
              : undefined;
          }
          transformedContentBlock.content = {
            title: block.title,
            anchorId: block.anchorId,
            images: images,
            mobileImages: mobileImages,
            colour: pageColour,
            narrowWidth: block.narrowWidth,
            firstImageLink: block.firstImageLink,
            firstImageOpenInNewTab: block.firstImageOpenInNewTab,
            secondImageLink: block.secondImageLink,
            secondImageOpenInNewTab: block.secondImageOpenInNewTab,
          };
        } else if (block.internal.type === 'ContentfulGlossaryManager') {
          transformedContentBlock.type = 'accordionBlock';

          const transformedItems = [];
          block.termsForGlossaryPage.forEach(term => {
            transformedItems.push({
              title: term.label,
              richTextContent: {
                glossaryTermAccordion: {
                  definition: term.definition.definition,
                  supportingDefinition:
                    term.supportingDefinition?.supportingDefinition,
                },
              },
              colour: pageColour,
            });
          });

          transformedContentBlock.content = {
            title: 'Definitions',
            anchorId: 'definitions-anchor',
            greyBackground: block.greyBackground,
            items: transformedItems,
          };
        } else if (block.internal.type === 'ContentfulAccordionBlock') {
          transformedContentBlock.type = 'accordionBlock';
          transformedContentBlock.content = transformAccordionBlock({
            block,
            localeCode,
            staticTranslations,
            pageColour,
            simplecastData,
            rawTooltipData,
          });
        } else if (block.internal.type === 'ContentfulCustomBlock') {
          transformedContentBlock.type = 'customBlock';
          transformedContentBlock.content = transformCustomBlock({ block });
        } else if (block.internal.type === 'ContentfulAlignedImageBlock') {
          transformedContentBlock.type = 'alignedImageBlock';
          transformedContentBlock.content = transformAlignedImageBlock({
            block,
            localeCode,
            pageColour,
            simplecastData,
            staticTranslations,
            rawTooltipData,
            videoMeta,
          });
        } else if (block.internal.type === 'ContentfulTabbedBlock') {
          transformedContentBlock.type = 'tabbedBlock';
          transformedContentBlock.content = transformTabbedBlock(
            block,
            pageColour,
            localeCode,
            staticTranslations,
            currentEntryId
          );
        } else if (block.internal.type === 'ContentfulTwoColumnTextBlock') {
          transformedContentBlock.type = 'twoColumnTextBlock';
          transformedContentBlock.content = {
            title: block.title,
            leftText: {
              raw: block.leftText ? block.leftText.raw : undefined,
              entryReferences:
                block.leftText && block.leftText.references
                  ? block.leftText.references
                      .filter(emptyObjectsFilter)
                      .map(
                        transformRichTextReference({
                          localeCode,
                          staticTranslations,
                          rawTooltipData,
                        })
                      )
                      .filter(emptyObjectsFilter)
                  : [],
            },
            rightText: {
              raw: block.rightText ? block.rightText.raw : undefined,
              entryReferences:
                block.rightText && block.rightText.references
                  ? block.rightText.references
                      .filter(emptyObjectsFilter)
                      .map(
                        transformRichTextReference({
                          localeCode,
                          staticTranslations,
                          rawTooltipData,
                        })
                      )
                      .filter(emptyObjectsFilter)
                  : [],
            },
            anchorId: block.anchorId,
            colour: block.useWhiteText ? '#ffffff' : '#2f2f46',
            backgroundColor: block.backgroundColor,
            backgroundImage: block.backgroundImage?.file
              ? {
                  src: block.backgroundImage.file.url,
                  alt: block.backgroundImage.description || '',
                  format: block.backgroundImage.file.contentType,
                  gatsbyImageData: block.backgroundImage.gatsbyImageData,
                }
              : undefined,
            mobileBackgroundImage: block.mobileBackgroundImage?.file
              ? {
                  src: block.mobileBackgroundImage.file.url,
                  alt: block.mobileBackgroundImage.description || '',
                  format: block.mobileBackgroundImage.file.contentType,
                  gatsbyImageData: block.mobileBackgroundImage.gatsbyImageData,
                }
              : undefined,
          };
        } else if (
          block.internal.type === 'ContentfulInteractiveMappingImage'
        ) {
          transformedContentBlock.type = 'interactiveMap';
          transformedContentBlock.content = {
            isFullWidth: block.isFullWidth,
            backgroundColor: block.backgroundColor,
            backgroundImage: block.backgroundImage?.file
              ? {
                  src: block.backgroundImage.file.url,
                  alt: block.backgroundImage.description || '',
                  format: block.backgroundImage.file.contentType,
                  gatsbyImageData: block.backgroundImage.gatsbyImageData,
                }
              : undefined,
            mobileBackgroundImage: block.mobileBackgroundImage?.file
              ? {
                  src: block.mobileBackgroundImage.file.url,
                  alt: block.mobileBackgroundImage.description || '',
                  format: block.mobileBackgroundImage.file.contentType,
                  gatsbyImageData: block.mobileBackgroundImage.gatsbyImageData,
                }
              : undefined,
            interactiveImage: {
              file: {
                url: block.interactiveImage?.file?.url,
                details: {
                  image: {
                    width: block.interactiveImage?.file?.details?.image?.width,
                    height:
                      block.interactiveImage?.file?.details?.image?.height,
                  },
                },
              },
              gatsbyImageData: block.interactiveImage?.gatsbyImageData,
              format: block.interactiveImage?.file?.contentType,
              description: block.interactiveImage?.description,
            },
            pinContentMapper:
              block.pinContentMapper?.map(pin => ({
                id: pin.id,
                title: pin.title,
                description: pin.description,
                pill: pin.pill,
                color: pin.color,
                useDarkPillText: pin.useDarkPillText,
                coordinates: {
                  x: pin.coordinates.x,
                  y: pin.coordinates.y,
                },
              })) || [],
          };
        } else if (block.internal.type === 'ContentfulSeparator') {
          transformedContentBlock.type = 'separatorBlock';
          transformedContentBlock.content = {
            isFullWidth: block.isFullWidth || false,
          };
        } else if (block.internal.type === 'ContentfulGalleryCarouselBlock') {
          transformedContentBlock.type = 'galleryCarouselBlock';
          transformedContentBlock.content = transformGalleyCarouselBlock(
            block,
            localeCode
          );
        } else {
          console.warn(`Block type not found: ${block.internal.type}`);
        }

        transformedFlexibleContent.push(transformedContentBlock);
      }
    });
    return { transformedFlexibleContent, videoMeta };
  } catch (error) {
    console.error(
      'Error in transform-data-functions.js -> transformFlexibleContent: ',
      error
    );
    return null;
  }
};

const transformTags = tags => {
  if (tags) {
    const filteredTags = tags.filter(tag => tag !== 'None applicable');
    const transformedTags = filteredTags.map(tag => ({
      title: tag,
    }));
    return transformedTags.length > 0 ? transformedTags : null;
  } else {
    return null;
  }
};

const transformSocialStaticImages = (
  mainImage,
  fallbackImage,
  englishFallbackImage
) => {
  let image;
  if (mainImage && mainImage.file) {
    image = generateImageUrl(mainImage.file.url);
  } else if (
    englishFallbackImage &&
    englishFallbackImage.metaAndSeo?.socialShareImage &&
    englishFallbackImage.metaAndSeo?.socialShareImage.file
  ) {
    image = generateImageUrl(
      englishFallbackImage.metaAndSeo?.socialShareImage.file.url
    );
  } else if (
    fallbackImage &&
    fallbackImage.mastheadImage &&
    fallbackImage.mastheadImage.file
  ) {
    image = generateImageUrl(fallbackImage.mastheadImage.file.url);
  } else if (
    englishFallbackImage &&
    englishFallbackImage.mastheadImage &&
    englishFallbackImage.mastheadImage.file
  ) {
    image = generateImageUrl(englishFallbackImage.mastheadImage.file.url);
  } else if (
    englishFallbackImage &&
    englishFallbackImage.mastHeadBackgroundImage &&
    englishFallbackImage.mastHeadBackgroundImage.file
  ) {
    image = generateImageUrl(
      englishFallbackImage.mastHeadBackgroundImage.file.url
    );
  } else {
    image = null;
    console.warn(
      'function transformSocialStaticImages: returned null - Social Image not set'
    );
  }
  return image;
};

const transformMastheadStaticImages = (mainImage, englishFallbackImage) => {
  let image;
  if (mainImage && mainImage.file) {
    image = generateImageUrl(mainImage.file.url);
  } else if (
    englishFallbackImage &&
    englishFallbackImage.mastheadImage &&
    englishFallbackImage.mastheadImage.file
  ) {
    image = generateImageUrl(englishFallbackImage.mastheadImage.file.url);
  } else if (
    englishFallbackImage &&
    englishFallbackImage.mastHeadBackgroundImage &&
    englishFallbackImage.mastHeadBackgroundImage.file
  ) {
    image = generateImageUrl(
      englishFallbackImage.mastHeadBackgroundImage.file.url
    );
  } else {
    console.warn(
      'function transformMastheadStaticImages: returned null - Masthead Image not set'
    );
    image = null;
  }
  return image;
};

const parseNode = node => {
  const { content, value } = node;
  if (content) {
    return content.map(parseNode).filter(content => content);
  } else {
    return value;
  }
};

const transformFlexibleContentToString = flexibleContent => {
  const flexibleContentParsed = JSON.parse(JSON.stringify(flexibleContent));
  let flexibleContentText = '';

  if (flexibleContentParsed && Array.isArray(flexibleContentParsed)) {
    flexibleContentParsed.forEach(block => {
      if (block.content && block.content.raw) {
        let rawData = block.content.raw;
        rawData = rawData.replace(/[\n\r]+/g, '');
        const parsedData = JSON.parse(rawData);
        flexibleContentText += parseNode(parsedData).join('');
      }
    });
  }
  return flexibleContentText;
};

const getHrefLangUrls = (pageUrl, pageLocale, localeLinks) => {
  let hrefLangSlugs = [{ url: pageUrl, localeCode: pageLocale }];
  if (pageLocale === 'en-GB') {
    hrefLangSlugs.push(
      {
        url: pageUrl,
        localeCode: 'x-default',
      },
      {
        url: pageUrl,
        localeCode: 'en',
      }
    );
  }
  Array.isArray(localeLinks) &&
    localeLinks.forEach(link => {
      hrefLangSlugs.push({
        url: normalizeTrailingSlash(link.url),
        localeCode: link.code,
      });
      if (link.code === 'en-GB') {
        hrefLangSlugs.push(
          {
            url: normalizeTrailingSlash(link.url),
            localeCode: 'x-default',
          },
          {
            url: normalizeTrailingSlash(link.url),
            localeCode: 'en',
          }
        );
      }
    });
  return hrefLangSlugs;
};

const getHomePageHrefLangUrls = localeLinks => {
  let hrefLangSlugs = [];
  const chineseHomeUrl = 'https://ellenmacarthurfoundation.org.cn';

  Array.isArray(localeLinks) &&
    localeLinks.forEach(link => {
      hrefLangSlugs.push({
        url: normalizeTrailingSlash(link.url),
        localeCode: link.code,
      });
      if (link.code === 'en-GB') {
        hrefLangSlugs.push(
          {
            url: normalizeTrailingSlash(link.url),
            localeCode: 'x-default',
          },
          {
            url: normalizeTrailingSlash(link.url),
            localeCode: 'en',
          }
        );
      }
    });

  hrefLangSlugs.push({
    url: chineseHomeUrl,
    localeCode: 'zh-CN',
  });

  return hrefLangSlugs;
};

const transformFlipcardBlock = ({
  block,
  pageColour,
  localeCode,
  simplecastData,
  staticTranslations,
  rawTooltipData,
  extraRichTextComponents,
}) => {
  const transformFlipcardRichText = content => {
    if (!content) return content;

    const entryReferences =
      content.references && Array.isArray(content.references)
        ? content.references
            .filter(emptyObjectsFilter)
            .map(
              transformRichTextReference({
                pageColour,
                localeCode,
                simplecastData,
                staticTranslations,
                rawTooltipData,
              })
            )
            .filter(emptyObjectsFilter)
        : [];

    return {
      ...content,
      entryReferences,
    };
  };

  const flipcardTiles = block.flipcardTiles
    ? block.flipcardTiles.map(tile => ({
        title: tile.title,
        hasBadge: tile.hasBadge,
        badgeBgColour: tile.badgeBgColour,
        badgeTextColour: tile.badgeTextColour,
        reverseBadgeBgColour: tile.reverseBadgeBgColour,
        reverseBadgeTextColour: tile.reverseBadgeTextColour,
        bgColour: tile.bgColour,
        reverseBgColour: tile.reverseBgColour,
        badgeText: tile.badgeText,

        // changed here
        content: transformFlipcardRichText(tile.content),

        bgImageSrc: tile.bgImage?.file?.url,
        bgImageAlt: tile.bgImage?.description,
        reverseBgImageSrc: tile.reverseBgImage?.file?.url,
        reverseBgImageAlt: tile.reverseBgImage?.description,
        hasDarkOverlay: tile.hasDarkOverlay,
        isFlippable: tile.isFlippable,
        isPortrait: tile.isPortrait,
        modalLink: tile.modalLink
          ? transformedModalBlock(tile.modalLink, {
              localeCode,
              pageColour,
              simplecastData,
              staticTranslations,
              rawTooltipData,
            })
          : null,
        linkUrl: tile.linkUrl,
        linkText: tile.linkText,
        textColour: tile.useGreyText ? '#2f2f46' : '#ffffff',
      }))
    : [];

  return {
    contentfulId: block.contentful_id,
    contentType: block.internal.type,
    flipcardTiles,
    title: block.title,
    anchorId: block.anchorId,
    description: block.description?.description,
    displayAsCarousel: block.displayAsCarousel,
    extraRichTextComponents: extraRichTextComponents,
  };
};

const transformGalleyCarouselBlock = (block, localeCode) => {
  const images = block?.images
    ? block.images.map(image => {
        const { description, internalLink, externalLink, imageSrc } = image;

        let linkUrl = externalLink;
        let openInNewTab = true;

        if (internalLink) {
          const entryData = {
            ...internalLink,
            contentType: internalLink.internal?.type,
          };
          const internalUrl = generateEntryUrl(localeCode, entryData);
          if (internalUrl) {
            linkUrl = internalUrl;
            openInNewTab = false;
          }
        }

        return {
          description,
          linkUrl,
          openInNewTab,
          gatsbyImageData: imageSrc?.gatsbyImageData,
          imageSrc: imageSrc?.file?.url,
          format: imageSrc?.file?.contentType,
          alt: imageSrc?.description,
        };
      })
    : [];

  return {
    contentfulId: block.contentful_id,
    contentType: block.internal.type,
    images: images,
    title: block.title,
    anchorId: block.anchorId,
    description: block.description?.description,
    isFullWidth: block.isFullWidth,
    enableLightText: block.enableLightText,
    backgroundColor: block.backgroundColor,
  };
};

module.exports = {
  transformHomepageContent,
  transformTileContent,
  getPodcastEmbedLink,
  getPagePodcastSlugs,
  getTileEnabledState,
  getTileRenderState,
  transformFlexibleContent,
  transformTags,
  transformSocialStaticImages,
  transformMastheadStaticImages,
  transformFlexibleContentToString,
  emptyObjectsFilter,
  getHrefLangUrls,
  getHomePageHrefLangUrls,
  transformRichTextReference,
  transformTopLevelRichText,
  transformSingleCtaBlockContent,
  transformTileBlockContent,
  transformInteractiveImageBlockContent,
  transformEmbedBlockContent,
  transformCircularExamplesBlock,
  transformExample,
  transformAutomatedTileListingBlock,
  transformAccordionBlock,
  transformLogoBlock,
  transformCustomBlock,
  transformedLegendBlock,
  transformAlignedImageBlock,
  transformGalleyCarouselBlock,
  transformedModalBlock,
};
